import { API_URL } from "@/config";
import { useState, useEffect } from "react";

export interface LegalNode {
  id: string;
  title: string;
  description: string;
  type: "PRIVACY_POLICY" | "TERM_CONDITION" | "COOKIE_POLICY";
}

interface UseFetchFooterLegalProps {
  tenantDomain?: string;
  apiVersion?: string;
}

interface UseFetchFooterLegalReturn {
  privacy: LegalNode | null;
  termAndCondition: LegalNode | null;
  cookiePolicy: LegalNode | null;
  isLoading: boolean;
}

/**
 * Simplified hook to fetch footer legal documents for end-user
 * Only calls getEndUserNodes API, no CMS logic
 */
export const useFetchFooterLegal = ({
  tenantDomain,
  apiVersion = "v1",
}: UseFetchFooterLegalProps): UseFetchFooterLegalReturn => {
  const [privacy, setPrivacy] = useState<LegalNode | null>(null);
  const [termAndCondition, setTermAndCondition] = useState<LegalNode | null>(
    null
  );
  const [cookiePolicy, setCookiePolicy] = useState<LegalNode | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const getNodes = async () => {
      setIsLoading(true);

      try {
        // Get access token and tenant domain from localStorage
        const accessToken = window.localStorage.getItem(
          "end_user_access_token"
        );
        const domain =
          tenantDomain || window.localStorage.getItem("tenant_domain") || "";

        // Prepare headers
        const headers: Record<string, string> = {
          "x-tenant-domain": domain,
        };

        if (accessToken) {
          headers.Authorization = `Bearer ${accessToken}`;
        }

        // Call API endpoint (similar to ShopifyApiService.getEndUserNodes)
        const response = await fetch(
          `${API_URL}/${apiVersion}/nodes?pageIndex=1&pageSize=10`,
          {
            method: "GET",
            headers,
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const nodes: LegalNode[] = data.items || [];

        if (nodes.length > 0) {
          setPrivacy(
            nodes.find((item) => item.type === "PRIVACY_POLICY") || null
          );
          setTermAndCondition(
            nodes.find((item) => item.type === "TERM_CONDITION") || null
          );
          setCookiePolicy(
            nodes.find((item) => item.type === "COOKIE_POLICY") || null
          );
        }
      } catch (error) {
        // Graceful degradation: return empty state if API fails
        console.warn("Failed to fetch footer legal documents:", error);
        setPrivacy(null);
        setTermAndCondition(null);
        setCookiePolicy(null);
      } finally {
        setIsLoading(false);
      }
    };

    getNodes();
  }, [tenantDomain, apiVersion]);

  return {
    privacy,
    termAndCondition,
    cookiePolicy,
    isLoading,
  };
};
